import { useEffect, useMemo } from "react";
import { useQueryState } from "nuqs";

import { clsxMerge } from "shared/lib/helpers";
import { UserIcon } from "shared/ui/icons";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";
import {
  PROSPECT_DISPOSITION_TYPES_MAP,
  PROSPECT_SYSTEM_DISPOSITION_TYPES_MAP,
  PROSPECT_SYSTEM_DISPOSITION_TYPES,
} from "shared/lib/constants/dispositions";
import { TooltipContent, TooltipTrigger } from "shared/ui/tooltip";
import { Tooltip } from "shared/ui/tooltip";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { createDisqualificationTooltipContent } from "shared/ui/account-details/disqualification-tooltip-content";
import { CONTACT_TYPES_LABEL_MAP } from "shared/lib/constants/lists";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { getLeadStateFlags } from "@/modules/pipeline/account-details/utils";
import { ProspectTag } from "@/modules/pipeline/account-details/shared/prospect-tag";
import { ProspectInfoBlock } from "@/modules/pipeline/account-details/shared/prospect-info-block";
import { ProspectEditMenu } from "@/modules/pipeline/account-details/shared/prospect-edit-menu/";
import { WIDGETS_SEARCH_QUERY_PARAM_NAMES } from "@/components/shared/widgets/constants";

export const AccountTargetedProspectSection = ({
  className,
}: PropsWithClassNameI) => {
  const [contactId, setContactId] = useQueryState(
    WIDGETS_SEARCH_QUERY_PARAM_NAMES.CONTACT_ID
  );

  /**
   * Workaround when useQueryState fail to detect update of contactId
   */
  useEffect(() => {
    if (!contactId) {
      const contactId = new URLSearchParams(window.location.search).get(
        WIDGETS_SEARCH_QUERY_PARAM_NAMES.CONTACT_ID
      );

      if (contactId) {
        setContactId(contactId);
      }
    }
  }, [contactId]);

  const { contacts } = useAccountDetailsContext();
  const contact = useMemo(
    () => contacts?.find((c) => c.id === contactId),
    [contacts, contactId]
  );

  const { isLeadDisqualified, isLeadSuspended } = useMemo(
    () =>
      contact
        ? getLeadStateFlags(contact)
        : { isLeadDisqualified: false, isLeadSuspended: false },
    [contact?.contact_disposition_type]
  );

  return contact ? (
    <AccountDetailsSectionContainer
      title="Lead"
      actions={
        <ProspectEditMenu contact={contact} className="relative top-1" />
      }
      className={clsxMerge(className)}
      headerClassName="bg-[#ECF8FF]"
    >
      <div className="flex gap-4 pb-2">
        <div className="flex flex-col items-center gap-2">
          <div
            className={clsxMerge(
              "flex items-center justify-center",
              "size-[76px] rounded-full bg-[#ECF8FF] p-2"
            )}
          >
            <UserIcon className="text-[#BCE2AE]" />
          </div>

          {contact.is_primary_lead && (
            <Tooltip showDelay={50}>
              <TooltipTrigger>
                <ProspectTag
                  tag="Primary Lead"
                  className="rounded-full bg-[#014DFE] text-white typography-body-5-semibold"
                />
              </TooltipTrigger>
              <TooltipContent className="z-50 max-w-[250px] text-center">
                A primary lead is the main contact for an account. They are the
                lead that gets dialed first when calling.
              </TooltipContent>
            </Tooltip>
          )}

          {isLeadDisqualified && (
            <ProspectTag
              className="rounded-full typography-body-5-semibold"
              tag="Disqualified"
              tooltip={createDisqualificationTooltipContent(
                contact.contact_disposition_type
                  ? CONTACT_TYPES_LABEL_MAP[
                      contact.contact_disposition_type as keyof typeof CONTACT_TYPES_LABEL_MAP
                    ]
                  : null,
                contact.dq_reason_notes
              )}
            />
          )}

          {isLeadSuspended && (
            <ProspectTag
              tag={
                PROSPECT_SYSTEM_DISPOSITION_TYPES_MAP[
                  PROSPECT_SYSTEM_DISPOSITION_TYPES.SUPPRESSED
                ]
              }
              className="rounded-full typography-body-5-semibold"
            />
          )}

          {!isLeadDisqualified &&
            !isLeadSuspended &&
            contact?.contact_disposition_type && (
              <ProspectTag
                className="rounded-full typography-body-5-semibold"
                tag={
                  PROSPECT_DISPOSITION_TYPES_MAP[
                    contact?.contact_disposition_type
                  ]
                }
              />
            )}
        </div>

        <ProspectInfoBlock contact={contact} hideTags />
      </div>
    </AccountDetailsSectionContainer>
  ) : null;
};
